import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { usersAPI, productsAPI, qrAPI, formatApiError, resolveImageUrl, investmentsAPI, uploadsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { User, Storefront, ArrowLeft, Plus, Pencil, Trash, Sun, Moon, QrCode, Eye, LinkSimple, Code, ShieldCheck, ChartLineUp, TrendUp, ArrowRight, Camera } from '@phosphor-icons/react';
import QRCodeCard from '../components/QRCodeCard';
import EmbedWidgetGenerator from '../components/EmbedWidgetGenerator';
import PageShell from '../components/PageShell';
import { toast } from 'sonner';
import { stripHtmlToText } from '../utils/text';
import { profileLink } from '../utils/profile';

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const { logo, isDark, toggleTheme, t } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({ name: '', username: '', bio: '', phone: '', theme: '' });
  const [qrStats, setQrStats] = useState(null);
  const [investments, setInvestments] = useState({ as_investor: [], as_seller: [] });

  useEffect(() => {
    if (user) setFormData({ name: user.name || '', username: user.username || '', bio: user.bio || '', phone: user.phone || '', theme: user.theme || '#22C55E' });
    loadProducts();
    loadQrStats();
    loadInvestments();
  }, [user]);

  const loadProducts = async () => {
    try { const response = await productsAPI.myProducts(); setProducts(response.data); } catch (err) { console.error('Failed to load products:', err); toast.error('Failed to load your listings'); }
  };

  const loadQrStats = async () => {
    try { const res = await qrAPI.getMyStats(); setQrStats(res.data); } catch (err) { console.error('Failed to load QR stats:', err); }
  };

  const loadInvestments = async () => {
    try { const { data } = await investmentsAPI.getMy(); setInvestments(data); } catch (err) { console.error('Failed to load investments:', err); }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Image must be under 3MB');
      return;
    }
    setUploadingAvatar(true);
    try {
      const { data } = await uploadsAPI.uploadAvatar(file);
      await usersAPI.updateProfile({ avatar_url: data.image_url });
      await refreshUser();
      toast.success('Profile picture updated!');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await usersAPI.updateProfile(formData); await refreshUser(); toast.success('Profile updated!'); } catch (error) { toast.error(formatApiError(error)); } finally { setLoading(false); }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    try { await productsAPI.delete(productId); await loadProducts(); toast.success('Product deleted'); } catch (error) { toast.error(formatApiError(error)); }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  return (
    <PageShell testId="profile-page" back>
      <div className="profile-content">
        <Link to="/dashboard" className="inline-flex items-center text-sm hover:underline mb-6 text-white/60" style={{ color: t.textSecondary }}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
        </Link>

        {/* Profile Header with $username */}
        <div className="flex items-center gap-4 mb-6">
          <label
            htmlFor="avatar-upload-input"
            className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden cursor-pointer group shrink-0 ring-2 ring-transparent hover:ring-[#22C55E] transition-all"
            data-testid="avatar-upload-label"
          >
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={user.name}
                className="w-full h-full object-cover"
                data-testid="profile-avatar-image"
              />
            ) : (
              <div className="w-full h-full bg-[#22C55E]/20 flex items-center justify-center text-[#22C55E] text-2xl font-bold" data-testid="profile-avatar">
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
            )}
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center transition-opacity">
              {uploadingAvatar ? (
                <span className="text-white text-[10px] font-semibold">Uploading...</span>
              ) : (
                <>
                  <Camera className="w-5 h-5 text-white" weight="fill" />
                  <span className="text-white text-[9px] font-semibold mt-0.5 leading-none">CHANGE</span>
                </>
              )}
            </div>
            <input
              id="avatar-upload-input"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAvatarUpload}
              disabled={uploadingAvatar}
              data-testid="avatar-upload-input"
            />
          </label>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight truncate" style={{ fontFamily: 'Cabinet Grotesk', color: t.text }} data-testid="profile-name-display">
              {user?.name || 'My Profile'}
            </h1>
            {user?.username && (
              <p className="text-[#22C55E] font-semibold" data-testid="profile-username-display">${user.username}</p>
            )}
            <p className="text-xs mt-0.5" style={{ color: t.textSecondary }}>Hover the avatar to upload a new picture</p>
          </div>
        </div>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6 border rounded-sm p-1" style={{ backgroundColor: t.cardBg, borderColor: t.border }}>
            <TabsTrigger value="profile" className="rounded-sm data-[state=active]:bg-[#22C55E] data-[state=active]:text-white text-xs sm:text-sm" style={{ color: t.text }} data-testid="profile-tab">
              <User className="w-4 h-4 mr-1 sm:mr-2" /> Profile
            </TabsTrigger>
            <TabsTrigger value="products" className="rounded-sm data-[state=active]:bg-[#22C55E] data-[state=active]:text-white text-xs sm:text-sm" style={{ color: t.text }} data-testid="products-tab">
              <Storefront className="w-4 h-4 mr-1 sm:mr-2" /> Products
            </TabsTrigger>
            <TabsTrigger value="investments" className="rounded-sm data-[state=active]:bg-[#22C55E] data-[state=active]:text-white text-xs sm:text-sm" style={{ color: t.text }} data-testid="investments-tab">
              <ChartLineUp className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Investments</span><span className="sm:hidden">Invest</span>
            </TabsTrigger>
            <TabsTrigger value="share" className="rounded-sm data-[state=active]:bg-[#22C55E] data-[state=active]:text-white text-xs sm:text-sm" style={{ color: t.text }} data-testid="share-tab">
              <Code className="w-4 h-4 mr-1 sm:mr-2" /> Share
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <Card className="border rounded-sm shadow-none" style={{ backgroundColor: t.cardBg, borderColor: t.border }} data-testid="profile-card">
              <CardHeader className="p-6 pb-4">
                <CardTitle className="text-lg font-bold" style={{ fontFamily: 'Cabinet Grotesk', color: t.text }}>Edit Profile</CardTitle>
              </CardHeader>
              <CardContent className="p-6 pt-0">
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.1em] uppercase font-semibold" style={{ color: t.textSecondary }}>Email</Label>
                    <Input type="email" value={user?.email || ''} disabled className="rounded-sm cursor-not-allowed" style={{ backgroundColor: t.bgSecondary, borderColor: t.border, color: t.textSecondary }} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.1em] uppercase font-semibold" style={{ color: t.textSecondary }}>Full Name</Label>
                    <Input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="rounded-sm" style={{ backgroundColor: t.inputBg, borderColor: t.border, color: t.text }} data-testid="profile-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.1em] uppercase font-semibold" style={{ color: t.textSecondary }}>Username (@handle)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#22C55E] font-semibold select-none">$</span>
                      <Input
                        type="text"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase() })}
                        placeholder="yourhandle"
                        className="rounded-sm pl-7"
                        style={{ backgroundColor: t.inputBg, borderColor: t.border, color: t.text }}
                        data-testid="profile-username-input"
                      />
                    </div>
                    <p className="text-xs" style={{ color: t.textMuted }}>3-20 characters, letters, numbers & underscores only</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.1em] uppercase font-semibold" style={{ color: t.textSecondary }}>Bio</Label>
                    <Textarea value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} placeholder="Tell people about yourself..." className="rounded-sm resize-none" rows={4} style={{ backgroundColor: t.inputBg, borderColor: t.border, color: t.text }} data-testid="profile-bio-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.1em] uppercase font-semibold" style={{ color: t.textSecondary }}>Phone (Optional)</Label>
                    <Input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+1 (555) 000-0000" className="rounded-sm" style={{ backgroundColor: t.inputBg, borderColor: t.border, color: t.text }} data-testid="profile-phone-input" />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-11 font-semibold" data-testid="save-profile-button">
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </form>

                {/* Theme Toggle */}
                <div className="mt-6 pt-6 border-t" style={{ borderColor: t.border }}>
                  <Label className="text-xs tracking-[0.1em] uppercase font-semibold mb-3 block" style={{ color: t.textSecondary }}>Appearance</Label>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => !isDark || toggleTheme()} className={`flex-1 rounded-sm ${!isDark ? 'border-[#22C55E] bg-[#22C55E]/10 text-[#22C55E]' : ''}`} style={{ borderColor: !isDark ? '#22C55E' : t.border, color: !isDark ? '#22C55E' : t.text }} data-testid="light-theme-button">
                      <Sun className="w-4 h-4 mr-2" /> Light
                    </Button>
                    <Button variant="outline" onClick={() => isDark || toggleTheme()} className={`flex-1 rounded-sm ${isDark ? 'border-[#22C55E] bg-[#22C55E]/10 text-[#22C55E]' : ''}`} style={{ borderColor: isDark ? '#22C55E' : t.border, color: isDark ? '#22C55E' : t.text }} data-testid="dark-theme-button">
                      <Moon className="w-4 h-4 mr-2" /> Dark
                    </Button>
                  </div>
                </div>

                {/* Storefront Theme Color */}
                <div className="mt-6 pt-6 border-t" style={{ borderColor: t.border }}>
                  <Label className="text-xs tracking-[0.1em] uppercase font-semibold mb-3 block" style={{ color: t.textSecondary }}>Storefront Theme</Label>
                  <p className="text-xs mb-3" style={{ color: t.textSecondary }}>Pick an accent color for your public storefront</p>
                  <div className="flex flex-wrap gap-2" data-testid="theme-color-picker">
                    {['#22C55E', '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#EF4444', '#06B6D4', '#F97316', '#6366F1', '#14B8A6'].map(color => (
                      <button
                        key={color}
                        onClick={async () => {
                          setFormData(prev => ({ ...prev, theme: color }));
                          try { await usersAPI.updateProfile({ theme: color }); await refreshUser(); toast.success('Store theme updated!'); } catch (err) { console.error('Store theme update failed:', err); toast.error('Failed to update store theme'); }
                        }}
                        className={`w-8 h-8 rounded-full border-2 transition-transform ${formData.theme === color ? 'scale-110 ring-2 ring-offset-2' : 'hover:scale-105'}`}
                        style={{ backgroundColor: color, borderColor: formData.theme === color ? color : 'transparent' }}
                        data-testid={`theme-color-${color.replace('#', '')}`}
                      />
                    ))}
                  </div>
                  {user?.username && (
                    <p className="text-xs mt-2" style={{ color: t.textSecondary }}>
                      Preview at <Link to={`/store/${user.username}`} className="hover:underline" style={{ color: formData.theme || '#22C55E' }}>/store/{user.username}</Link>
                    </p>
                  )}
                </div>

                {/* Security & Privacy */}
                <div className="mt-6 pt-6 border-t" style={{ borderColor: t.border }}>
                  <Label className="text-xs tracking-[0.1em] uppercase font-semibold mb-3 block" style={{ color: t.textSecondary }}>Security & Privacy</Label>
                  <div className="space-y-2">
                    <Link to="/settings/2fa" className="flex items-center gap-2 text-[#22C55E] hover:underline text-sm" data-testid="2fa-settings-link">
                      <ShieldCheck className="w-4 h-4" /> Two-Factor Authentication
                    </Link>
                    <Link to="/settings/privacy" className="flex items-center gap-2 text-[#22C55E] hover:underline text-sm" data-testid="privacy-settings-link">
                      <Eye className="w-4 h-4" /> Privacy & Data
                    </Link>
                  </div>
                </div>

                {/* Public Profile Links */}
                <div className="mt-6 pt-6 border-t" style={{ borderColor: t.border }}>
                  <Label className="text-xs tracking-[0.1em] uppercase font-semibold mb-2 block" style={{ color: t.textSecondary }}>Your Links</Label>
                  <div className="space-y-2">
                    <Link to={profileLink(user)} className="text-[#22C55E] hover:underline text-sm block" data-testid="public-profile-link">View public profile</Link>
                    {user?.username && (
                      <Link to={`/store/${user.username}`} className="text-[#22C55E] hover:underline text-sm block" data-testid="storefront-link">
                        View storefront (divispend.com/store/${user.username})
                      </Link>
                    )}
                  </div>
                </div>

                {/* QR Codes & Scan Stats */}
                <div className="mt-6 pt-6 border-t" style={{ borderColor: t.border }}>
                  <Label className="text-xs tracking-[0.1em] uppercase font-semibold mb-3 block" style={{ color: t.textSecondary }}>
                    <QrCode className="w-4 h-4 inline mr-2 text-[#22C55E]" weight="duotone" />
                    QR Codes
                  </Label>
                  
                  {/* Scan Stats Summary */}
                  {qrStats && (
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="p-3 rounded-sm" style={{ backgroundColor: t.bgSecondary }}>
                        <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: t.textSecondary }}>Profile Scans</p>
                        <p className="text-xl font-bold text-[#22C55E]" data-testid="profile-scan-count">{qrStats.profile_scans}</p>
                      </div>
                      <div className="p-3 rounded-sm" style={{ backgroundColor: t.bgSecondary }}>
                        <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: t.textSecondary }}>Product Scans</p>
                        <p className="text-xl font-bold text-[#22C55E]" data-testid="product-scan-total">{qrStats.total_product_scans}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Profile QR */}
                  {user?.username && (
                    <QRCodeCard
                      type="profile"
                      targetId={user.username}
                      title="My Storefront QR"
                      subtitle={`Share to get visitors to your store`}
                      showStats={true}
                    />
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="products">
            <div className="mb-4 flex justify-end">
              <Link to="/products/new">
                <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm" data-testid="add-product-button">
                  <Plus className="w-4 h-4 mr-2" /> Add Product/Service
                </Button>
              </Link>
            </div>
            {products.length === 0 ? (
              <Card className="border rounded-sm shadow-none" style={{ backgroundColor: t.cardBg, borderColor: t.border }}>
                <CardContent className="p-12 text-center">
                  <Storefront className="w-16 h-16 mx-auto mb-4" style={{ color: t.border }} />
                  <p className="mb-4" style={{ color: t.textSecondary }}>No products or services yet</p>
                  <Link to="/products/new"><Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm"><Plus className="w-4 h-4 mr-2" /> Add Your First Product</Button></Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {products.map((product) => (
                  <Card key={product.product_id} className="border rounded-sm shadow-none" style={{ backgroundColor: t.cardBg, borderColor: t.border }} data-testid={`product-${product.product_id}`}>
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {product.image_url ? (
                          <img src={resolveImageUrl(product.image_url)} alt={product.name} className="w-20 h-20 object-cover rounded-sm" style={{ backgroundColor: t.bgSecondary }} />
                        ) : (
                          <div className="w-20 h-20 rounded-sm flex items-center justify-center" style={{ backgroundColor: t.bgSecondary }}><Storefront className="w-8 h-8" style={{ color: t.border }} /></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-bold" style={{ fontFamily: 'Cabinet Grotesk', color: t.text }}>{product.name}</h3>
                              <p className="text-sm mt-1 line-clamp-2" style={{ color: t.textSecondary }}>{stripHtmlToText(product.description)}</p>
                              <div className="flex items-center gap-3 mt-2">
                                <span className="text-lg font-bold text-[#22C55E]">{formatCurrency(product.price)}</span>
                                <span className="text-xs px-2 py-1 rounded-sm" style={{ backgroundColor: t.bgSecondary, color: t.textSecondary }}>{product.category}</span>
                                {!product.available && <span className="text-xs bg-[#FF3B30]/10 text-[#FF3B30] px-2 py-1 rounded-sm">Unavailable</span>}
                                {qrStats?.products?.find(p => p.product_id === product.product_id)?.scan_count > 0 && (
                                  <span className="text-xs text-[#22C55E] flex items-center gap-1"><Eye className="w-3 h-3" />{qrStats.products.find(p => p.product_id === product.product_id).scan_count} scans</span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Link to={`/products/edit/${product.product_id}`}><Button variant="ghost" size="sm" style={{ color: t.textSecondary }}><Pencil className="w-4 h-4" /></Button></Link>
                              <Button variant="ghost" size="sm" className="text-[#FF3B30] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10" onClick={() => handleDeleteProduct(product.product_id)} data-testid={`delete-product-${product.product_id}`}><Trash className="w-4 h-4" /></Button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t" style={{ borderColor: t.border }}>
                        <QRCodeCard
                          type="product"
                          targetId={product.product_id}
                          title={`QR: ${product.name}`}
                          subtitle="Share to let people buy this product"
                          showStats={true}
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="investments" data-testid="investments-content">
            <div className="space-y-6">
              {/* Investments I've Made */}
              <Card className="border rounded-sm shadow-none" style={{ backgroundColor: t.cardBg, borderColor: t.border }}>
                <CardHeader className="p-6 pb-4">
                  <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk', color: t.text }}>
                    <TrendUp className="w-5 h-5 text-[#22C55E]" />
                    My Investments
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 pt-0">
                  {investments.as_investor.length === 0 ? (
                    <div className="text-center py-8">
                      <ChartLineUp className="w-12 h-12 mx-auto mb-3" style={{ color: t.border }} />
                      <p style={{ color: t.textSecondary }}>You haven't invested in any products yet</p>
                      <Link to="/explore"><Button size="sm" className="mt-3 bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm">Browse Marketplace</Button></Link>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {investments.as_investor.map((inv) => (
                        <Link key={inv.investment_id} to={`/product/${inv.product_id}`} className="flex items-center gap-4 p-4 rounded-sm transition-colors hover:opacity-80" style={{ backgroundColor: t.bgSecondary }} data-testid={`my-investment-${inv.investment_id}`}>
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#22C55E]/10">
                            <ChartLineUp className="w-5 h-5 text-[#22C55E]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm truncate" style={{ color: t.text }}>{inv.product_name}</p>
                            <p className="text-xs" style={{ color: t.textSecondary }}>
                              by {inv.seller?.name || 'Seller'} {inv.seller?.username ? `($${inv.seller.username})` : ''}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-sm font-bold text-[#22C55E]">{inv.percentage}% share</p>
                            <p className="text-[10px]" style={{ color: t.textSecondary }}>
                              ${inv.total_earned?.toFixed(2)} / ${inv.amount_invested?.toFixed(2)}
                            </p>
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${inv.status === 'active' ? 'bg-[#22C55E]/10 text-[#22C55E]' : 'bg-[#22C55E]/10 text-[#22C55E]'}`}>
                              {inv.status === 'active' ? 'Active' : 'Completed'}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Investors in My Products */}
              <Card className="border rounded-sm shadow-none" style={{ backgroundColor: t.cardBg, borderColor: t.border }}>
                <CardHeader className="p-6 pb-4">
                  <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk', color: t.text }}>
                    <User className="w-5 h-5 text-[#22C55E]" />
                    Investors in My Products
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 pt-0">
                  {investments.as_seller.length === 0 ? (
                    <div className="text-center py-8">
                      <User className="w-12 h-12 mx-auto mb-3" style={{ color: t.border }} />
                      <p style={{ color: t.textSecondary }}>No one has invested in your products yet</p>
                      <p className="text-xs mt-1" style={{ color: t.textMuted }}>Enable DiviSpend on a product to attract investors</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {investments.as_seller.map((inv) => (
                        <div key={inv.investment_id} className="flex items-center gap-4 p-4 rounded-sm" style={{ backgroundColor: t.bgSecondary }} data-testid={`investor-in-my-product-${inv.investment_id}`}>
                          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#22C55E]/10">
                            {inv.investor?.avatar_url ? (
                              <img src={inv.investor.avatar_url} alt="" className="w-10 h-10 rounded-full" />
                            ) : (
                              <span className="text-[#22C55E] font-bold text-sm">{inv.investor?.name?.[0] || '?'}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm truncate" style={{ color: t.text }}>{inv.investor?.name || 'Investor'}</p>
                            <p className="text-xs" style={{ color: t.textSecondary }}>
                              Invested in <span className="font-medium" style={{ color: t.text }}>{inv.product_name}</span>
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-sm font-bold text-[#22C55E]">${inv.amount_invested?.toFixed(2)}</p>
                            <p className="text-[10px]" style={{ color: t.textSecondary }}>{inv.percentage}% share</p>
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${inv.status === 'active' ? 'bg-[#22C55E]/10 text-[#22C55E]' : 'bg-[#22C55E]/10 text-[#22C55E]'}`}>
                              {inv.status === 'active' ? 'Active' : 'Completed'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="share">
            <div className="space-y-6">
              {/* Payment Link Section */}
              <Card className="border rounded-sm shadow-none" style={{ backgroundColor: t.cardBg, borderColor: t.border }} data-testid="pay-link-section">
                <CardHeader className="p-6 pb-4">
                  <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk', color: t.text }}>
                    <LinkSimple className="w-5 h-5 text-[#22C55E]" weight="duotone" />
                    Payment Link
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 pt-0 space-y-4">
                  <p className="text-sm" style={{ color: t.textSecondary }}>
                    Share your payment link so anyone can pay you directly. Works for tips, invoices, and payments.
                  </p>
                  <div className="flex gap-2">
                    <div className="flex-1 rounded-sm p-3 text-sm font-mono break-all" style={{ backgroundColor: t.bgSecondary, color: t.text, border: `1px solid ${t.border}` }} data-testid="pay-link-url">
                      {window.location.origin}/pay/{user?.username}
                    </div>
                    <Button variant="outline" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/pay/${user?.username}`); toast.success('Payment link copied!'); }} className="rounded-sm" style={{ borderColor: t.border, color: t.text }} data-testid="copy-pay-link">
                      Copy
                    </Button>
                  </div>
                  <div className="text-xs space-y-1" style={{ color: t.textMuted }}>
                    <p>With preset amount: <code style={{ color: '#22C55E' }}>/pay/{user?.username}?amount=25.00</code></p>
                    <p>With note: <code style={{ color: '#22C55E' }}>/pay/{user?.username}?amount=10&note=Coffee</code></p>
                  </div>
                </CardContent>
              </Card>

              {/* Embeddable Widget */}
              {user?.username && (
                <EmbedWidgetGenerator username={user.username} t={t} />
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </PageShell>
  );
}
